import {
  collection,
  doc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { bookingWindowFrom } from '../utils/bookingWindow';
import { getBookingLeadMinutes } from './liveLessons';
import { db } from '../firebase';
import type { CoachAvailabilitySlot, LiveOfferingType } from '../types/liveCall';

// ── Helpers ───────────────────────────────────────────────────────────────────

function normalize(data: Record<string, unknown>, id: string): CoachAvailabilitySlot {
  const toISO = (v: unknown): string =>
    v instanceof Timestamp ? v.toDate().toISOString() : (typeof v === 'string' ? v : new Date().toISOString());
  return {
    ...(data as unknown as CoachAvailabilitySlot),
    id,
    startAt: toISO(data.startAt),
    endAt: toISO(data.endAt),
    createdAt: toISO(data.createdAt),
  };
}

// ── Create ────────────────────────────────────────────────────────────────────

export async function addAvailabilitySlot(
  coachId: string,
  startAt: string,
  durationMinutes: number,
  offeringTypes?: LiveOfferingType[],
): Promise<CoachAvailabilitySlot> {
  const slotRef = doc(collection(db, 'coachAvailabilitySlots'));
  const endAt = new Date(new Date(startAt).getTime() + durationMinutes * 60_000).toISOString();

  const slot: Omit<CoachAvailabilitySlot, 'id'> = {
    coachId,
    startAt,
    endAt,
    durationMinutes,
    isBooked: false,
    offeringTypes,
    createdAt: new Date().toISOString(),
  };

  const cleaned = Object.fromEntries(Object.entries(slot).filter(([, v]) => v !== undefined));
  await setDoc(slotRef, { ...cleaned, createdAt: serverTimestamp() });

  return { ...slot, id: slotRef.id };
}

// ── Read ──────────────────────────────────────────────────────────────────────

/**
 * Returns available (unbooked) slots for a coach, optionally filtered by offering type,
 * starting from `fromDate` (defaults to now).
 */
export async function getAvailableSlotsForCoach(
  coachId: string,
  fromDate?: string,
  offeringType?: LiveOfferingType,
): Promise<CoachAvailabilitySlot[]> {
  // Section 5: from the server's lead time to 14 days ahead. The lead time
  // comes from getLiveBookingContext (bookingWindow.minMinutesBefore), read
  // beside the slots rather than before them so it adds no wait; the slots
  // are read from now and cut to it after.
  const now = new Date();
  const q = query(
    collection(db, 'coachAvailabilitySlots'),
    where('coachId', '==', coachId),
    where('isBooked', '==', false),
    where('startAt', '>=', fromDate ?? now.toISOString()),
    orderBy('startAt', 'asc'),
  );
  const [snap, lead] = await Promise.all([getDocs(q), getBookingLeadMinutes(coachId)]);
  const window = bookingWindowFrom(now, lead);
  const cutoff = fromDate ?? window.earliestStartAt;
  const slots = snap.docs.map((d) => normalize(d.data(), d.id))
    .filter((s) => s.startAt >= cutoff && s.startAt <= window.latestStartAt);
  if (!offeringType) return slots;
  return slots.filter(
    (s) => !s.offeringTypes || s.offeringTypes.includes(offeringType),
  );
}

/** All slots (booked and available) for a coach in a date range — for the coach's calendar view. */
export async function getCoachSlotRange(
  coachId: string,
  startAt: string,
  endAt: string,
): Promise<CoachAvailabilitySlot[]> {
  const q = query(
    collection(db, 'coachAvailabilitySlots'),
    where('coachId', '==', coachId),
    where('startAt', '>=', startAt),
    where('startAt', '<', endAt),
    orderBy('startAt', 'asc'),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => normalize(d.data(), d.id));
}

// ── Lock / unlock ─────────────────────────────────────────────────────────────

/** Lock a slot when a booking is being created (optimistic; Cloud Function validates). */
export async function lockSlot(slotId: string, bookingId: string): Promise<void> {
  await updateDoc(doc(db, 'coachAvailabilitySlots', slotId), {
    isBooked: true,
    bookingId,
    updatedAt: serverTimestamp(),
  });
}

/** Release a slot when a booking is cancelled before the call. */
export async function unlockSlot(slotId: string): Promise<void> {
  await updateDoc(doc(db, 'coachAvailabilitySlots', slotId), {
    isBooked: false,
    bookingId: null,
    updatedAt: serverTimestamp(),
  });
}

// ── Delete ────────────────────────────────────────────────────────────────────

export async function deleteAvailabilitySlot(slotId: string): Promise<void> {
  await deleteDoc(doc(db, 'coachAvailabilitySlots', slotId));
}

/** Mark a range of dates as unavailable (away dates) by deleting all unbooked slots in the range. */
export async function markCoachAway(
  coachId: string,
  fromISO: string,
  toISO: string,
): Promise<void> {
  const slots = await getCoachSlotRange(coachId, fromISO, toISO);
  await Promise.all(
    slots.filter((s) => !s.isBooked).map((s) => deleteAvailabilitySlot(s.id)),
  );
}
